/*************************************************************************
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {
  getPageTransformMatrix,
  getPageZoomLevel,
  getViewRotationMatrix
} from "../selectors/commonAppSelectors";
import { isUndefined } from "./DataTypeUtils";

export function calculateRectRelativeToPageRect(rect, pageRect) {
  // Extract properties from the rectangles
  const {
    bottom: rectBottom,
    left: rectLeft,
    right: rectRight,
    top: rectTop
  } = rect;
  const { left: pageLeft, top: pageTop } = pageRect;

  // Calculate width and height for rect
  const rectWidth = rectRight - rectLeft;
  const rectHeight = rectBottom - rectTop;

  // Calculate relative position
  const relativeLeft = rectLeft - pageLeft;
  const relativeTop = rectTop - pageTop;

  // Return the relative rectangle with the position and size
  return {
    left: relativeLeft,
    top: relativeTop,
    right: relativeLeft + rectWidth,
    bottom: relativeTop + rectHeight,
    width: rectWidth,
    height: rectHeight
  };
}
export const transformViewToPDF = (
  x,
  y,
  pageIndex,
  stores,
  zoomLevel,
  options = {}
) => {
  const tMatrix = getPageTransformMatrix(stores, pageIndex, options);
  const scale = zoomLevel || getPageZoomLevel(stores, pageIndex);
  const tempX = x / scale;
  const tempY = y / scale;
  const val = tMatrix.a * tMatrix.d - tMatrix.b * tMatrix.c;
  const retX =
    (tMatrix.d * tempX -
      tMatrix.c * tempY +
      tMatrix.c * tMatrix.f -
      tMatrix.d * tMatrix.e) /
    val;
  const retY =
    (tMatrix.a * tempY -
      tMatrix.b * tempX +
      tMatrix.b * tMatrix.e -
      tMatrix.a * tMatrix.f) /
    val;
  return { x: retX, y: retY };
};

export const transformPDFToView = (
  x,
  y,
  pageIndex,
  stores,
  zoomLevel,
  options = {}
) => {
  const tMatrix = getPageTransformMatrix(stores, pageIndex, options);
  if (!tMatrix) {
    return { x: 0, y: 0 };
  }
  let retX = tMatrix.a * x + tMatrix.c * y + tMatrix.e;
  let retY = tMatrix.b * x + tMatrix.d * y + tMatrix.f;
  const scale = zoomLevel || getPageZoomLevel(stores, pageIndex);
  retX = Math.round(retX * scale);
  retY = Math.round(retY * scale);
  return { x: retX, y: retY };
};

export const transformViewToRotatedView = (
  x,
  y,
  pageIndex,
  stores,
  zoomLevel,
  rotationAngle
) => {
  const viewRotationMatrix = getViewRotationMatrix(
    stores,
    pageIndex,
    rotationAngle
  );
  const pageZoomLevel = getPageZoomLevel(stores, pageIndex);
  const scale = pageZoomLevel / (zoomLevel || pageZoomLevel);
  const retX = Math.round(x * scale);
  const retY = Math.round(y * scale);
  return {
    x: Math.round(
      (viewRotationMatrix.a * retX +
        viewRotationMatrix.c * retY +
        viewRotationMatrix.e) /
        scale
    ),
    y: Math.round(
      (viewRotationMatrix.b * retX +
        viewRotationMatrix.d * retY +
        viewRotationMatrix.f) /
        scale
    )
  };
};

export const getTransformMatrixFromPageGeometry = data => {
  const { rotation, boxes, shouldUseMediaBox } = data;
  const box = shouldUseMediaBox ? boxes.mediaBox : boxes.cropBox;
  const [xmin, ymin, xmax, ymax] = box;
  let tMatrix = {};
  if (!isUndefined(rotation)) {
    switch (rotation) {
      case 270:
        tMatrix = { a: 0, b: -1, c: -1, d: 0, e: ymax, f: xmax };
        break;
      case 180:
        tMatrix = { a: -1, b: 0, c: 0, d: 1, e: xmax, f: -ymin };
        break;
      case 90:
        tMatrix = { a: 0, b: 1, c: 1, d: 0, e: -ymin, f: -xmin };
        break;
      default:
        tMatrix = { a: 1, b: 0, c: 0, d: -1, e: -xmin, f: ymax };
        break;
    }
  }
  return tMatrix;
};

/* 
    This function only converts individual pairs of points in an array.
    The array is of form [x1, y1, x2, y2, x3, y3, x4, y4], where x,y coordinates are
    in the order as shown below. 
  
    (x1,y1) .----. (x2,y2)
            |    |
    (x4,y4) .----. (x3,y3)
    The returned array is of the form [tx1, ty1, tx2, ty2, tx3, ty3, tx4, ty4]
  */
export const transformQuadFromPDFToView = (
  quadArray,
  pageIndex,
  stores,
  zoomLevel
) => {
  const transformedQuad = [];
  for (let i = 0; i < 8; i += 2) {
    const x = quadArray[i];
    const y = quadArray[i + 1];
    const obj = transformPDFToView(x, y, pageIndex, stores, zoomLevel);
    transformedQuad.push(obj.x, obj.y);
  }
  return transformedQuad;
};

export const transformQuadFromViewToRotatedView = (
  quadArray,
  pageIndex,
  stores,
  zoomLevel,
  rotationAngle
) => {
  const transformedQuad = [];
  for (let i = 0; i < 8; i += 2) {
    const x = quadArray[i];
    const y = quadArray[i + 1];
    const obj = transformViewToRotatedView(
      x,
      y,
      pageIndex,
      stores,
      zoomLevel,
      rotationAngle
    );
    transformedQuad.push(obj.x, obj.y);
  }
  return transformedQuad;
};
